// attachment-picker.js — platform-aware file picker for the /attach
// slash command and any future toolbar entry.
//
//   • web + electron — hidden <input type="file" multiple>
//   • capacitor (iOS) — @capacitor/camera Camera.pickImages for photos,
//                       @capacitor/filesystem pickFiles for documents.
//
// Every selected file is forwarded to AttachmentInput.attachFile, which
// owns size limits, image transcoding, sanitization, and autosave. This
// module just produces File objects.

let AttachmentPicker: any;

(() => {
  /**
   * @param {any} file
   * @returns {Promise<any>}
   */
  function _attach(file: any) {
    if (!file) return Promise.resolve(false);
    return window.AttachmentInput?.attachFile?.(file) ?? Promise.resolve(false);
  }

  // Web + Electron: a single hidden input lives at the bottom of <body>; a
  // fresh picker dialog opens each time we click() it. Reusing the input
  // saves DOM churn but means we have to clear .value on every cycle so
  // re-picking the same file still fires 'change'.
  let _hiddenInput: any = null;
  /** @returns {HTMLInputElement} the shared hidden file input. */
  function _ensureInput() {
    if (_hiddenInput) return _hiddenInput;
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.style.position = 'absolute';
    input.style.left = '-9999px';
    input.style.width = '1px';
    input.style.height = '1px';
    input.style.opacity = '0';
    input.setAttribute('aria-hidden', 'true');
    input.addEventListener('change', async () => {
      const files = Array.from(input.files || []);
      input.value = '';
      for (const f of files) await _attach(f);
    });
    document.body.appendChild(input);
    _hiddenInput = input;
    return input;
  }

  /** @returns {Promise<void>} */
  async function _pickViaInput() {
    _ensureInput().click();
  }

  /** @returns {Promise<any>} */
  async function _pickViaCapacitor() {
    const Plugins = window.Capacitor?.Plugins || {};
    const Camera = Plugins.Camera;
    if (!Camera || typeof Camera.pickImages !== 'function') {
      // No Camera plugin available — fall through to the input flow so the
      // user still gets a way to attach (Capacitor's WKWebView happily
      // surfaces the standard file picker for HTML <input type=file>).
      return _pickViaInput();
    }
    let result: any;
    try {
      result = await Camera.pickImages({ quality: 90, limit: 0 });
    } catch (e: any) {
      // User cancellation or denied permission — silent.
      console.warn('[attachment-picker] Camera.pickImages failed:', e?.message || e);
      return;
    }
    const photos = result?.photos || [];
    for (const p of photos) {
      try {
        const url = p.webPath || p.path;
        if (!url) continue;
        const resp = await fetch(url);
        const blob = await resp.blob();
        const guessedExt = blob.type?.split('/')[1] || 'jpg';
        const name = `photo_${Date.now()}.${guessedExt}`;
        await _attach(new File([blob], name, { type: blob.type || 'image/jpeg' }));
      } catch (e: any) {
        console.error('[attachment-picker] failed to read picked image:', e);
      }
    }
  }

  /** @returns {Promise<any>} */
  async function pick() {
    if (window.Platform?.isCapacitor) return _pickViaCapacitor();
    return _pickViaInput();
  }

  AttachmentPicker = { pick };
})();

export { AttachmentPicker };
